import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DECIMAL_RE } from "@/lib/format";

const blank = { date: "", description: "", director: "", debit: "", credit: "", split: false, directors: [], mode: "same" };
const amt = (v) => { const s = String(v ?? "").replace(/,/g, "").trim(); return s === "" || Number(s) === 0 ? "0" : s; };

export function validateEntry(f) {
  if (!f.date) return "Enter a date";
  const [d, c] = [amt(f.debit), amt(f.credit)];
  if (!DECIMAL_RE.test(d) || !DECIMAL_RE.test(c)) return "Debit and Credit must be numbers";
  if (d !== "0" && c !== "0") return "An entry can have a Debit or a Credit, not both";
  if (d === "0" && c === "0") return "Enter a Debit or Credit amount";
  if (f.split && f.directors.length < 2) return "Pick at least two directors to split between";
  if (!f.split && !f.director) return "Pick a director";
  return null;
}

export default function EntryDialog({ open, onOpenChange, initial, directors, onSubmit }) {
  const [f, setF] = useState(blank);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF({ ...blank, ...initial }); setErr(""); } }, [open, initial]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const editing = !!initial?.id;
  const toggleDir = (id) => setF((p) => ({ ...p, directors: p.directors.includes(id) ? p.directors.filter((x) => x !== id) : [...p.directors, id] }));

  const submit = async (e) => {
    e.preventDefault();
    const v = validateEntry(f);
    if (v) return setErr(v);
    setBusy(true);
    const msg = await onSubmit({ ...f, debit: amt(f.debit), credit: amt(f.credit) });
    setBusy(false);
    if (msg) setErr(msg);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card" data-testid="entry-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit entry" : "Add director entry"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Date</Label><Input type="date" value={f.date} onChange={set("date")} className="font-mono" data-testid="entry-date-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={f.description} onChange={set("description")} data-testid="entry-description-input" /></div>
            <div className="space-y-2"><Label>Debit</Label><Input value={f.debit} onChange={set("debit")} inputMode="decimal" className="font-mono" data-testid="entry-debit-input" /></div>
            <div className="space-y-2"><Label>Credit</Label><Input value={f.credit} onChange={set("credit")} inputMode="decimal" className="font-mono" data-testid="entry-credit-input" /></div>
          </div>
          {!editing && (
            <div className="flex items-center justify-between border border-border p-3">
              <Label htmlFor="entry-split">Split between directors</Label>
              <Switch id="entry-split" checked={f.split} onCheckedChange={(v) => setF((p) => ({ ...p, split: v }))} data-testid="entry-split-switch" />
            </div>
          )}
          {f.split ? (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-4">
                {directors.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={f.directors.includes(d.id)} onCheckedChange={() => toggleDir(d.id)} data-testid={`entry-split-director-${d.name}`} /> {d.name}
                  </label>
                ))}
              </div>
              <Select value={f.mode} onValueChange={(v) => setF((p) => ({ ...p, mode: v }))}>
                <SelectTrigger data-testid="entry-split-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="same">Same amount charged to each</SelectItem>
                  <SelectItem value="divide">Divide the amount equally</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Director</Label>
              <Select value={f.director} onValueChange={(v) => setF((p) => ({ ...p, director: v }))}>
                <SelectTrigger data-testid="entry-director-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{directors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {err && <p className="text-sm text-destructive" data-testid="entry-error">{err}</p>}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="entry-save-button">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
